import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Form, Pagination, Row, Select, Space, Table, Typography, message, Modal as AntModal } from "antd";
import type { PaginationProps, TableProps } from "antd";
import "./HeatingValue.css";
import { BsPencilSquare, BsTrashFill } from "react-icons/bs";
import { usePagination } from "src/hook/usePagination";
import { useDidMount } from "src/hook/useDidMount";
import { useShallow } from "zustand/react/shallow";
import { PlusCircleOutlined } from "@ant-design/icons";
import { replaceUndefinedWithNull, trimAllKey } from "src/utils/utils-object";
import { CommonSuccess } from "src/entity/common/menus-enum.enum";
import { flareHeatingStore } from "src/stores/flare-heating-store";
import {
  CreateHeatingValue,
  FilterSetupHeatingValue,
  ManageCompoundProps,
  ResponseHeatingValue,
} from "src/entity/flare/heating-value.entity";
import { Composition, CompositionDropdown } from "src/entity/flare/flare.constants";
import { isNull, omitBy, uniqueId } from "lodash";
import HeatingModal from "./HeatingModal";
import { profileStore } from "src/stores/profile-store";
import { formatNumber, parseAndFormatScientificNumber, parseToDecimal } from "src/utils/process-number";
import { UNITS_ENUM } from "src/entity/common/unit.constant";
import VocsModal from "src/components/Modal";
import { reportFlareStore } from "src/stores/report-flare-volume-store";
import NumberRow from "./NumberRow";
import useNavigationMenu from "src/hook/useNavigationMenu";
import { canEditTable } from "src/pages/VocsInventory/Flare/PermissionCheck/PermissionCheck";
import { optionsSelectYears } from "src/utils/array";
import Search, { SearchProps } from "antd/es/input/Search";
import { addCenterAlign } from "src/components/AntdCustomPart/TableTitle.tsx/TableTitle";

const { Text } = Typography;
interface HeatingValueTableProps {}

const HeatingValueTable: React.FC<HeatingValueTableProps> = ({}) => {
  const currentYear = new Date().getFullYear();
  const [messageApi, contextHolder] = message.useMessage();
  const [formSetupHeatingValue] = Form.useForm();

  const prevCompany: any = useRef();

  const [selectedYear, setSelectedYear] = useState<string>(currentYear.toString());
  const [visibleModal, setVisibleModal] = useState(false);
  const [isEdit, setIsEdit] = useState<ResponseHeatingValue | null>(null);
  const [manageCompound, setManageCompound] = useState<ManageCompoundProps[]>([]);
  const [tableKey, setTableKey] = useState(0);
  const { permissions } = useNavigationMenu();

  const profile = profileStore(useShallow((state) => state.profile));
  const loadingProfile = profileStore(useShallow((state) => state.loading));

  const loadingHeating = flareHeatingStore(useShallow((state) => state.loadingHeating));
  const getAllSettingFlareCompound = flareHeatingStore(useShallow((state) => state.getAllSettingFlareCompound));
  const searchSetupHeatingValue = flareHeatingStore(useShallow((state) => state.searchSetupHeatingValue));
  const dataHeating = flareHeatingStore(useShallow((state) => state.dataHeating));
  const totalDocs = dataHeating.total;
  const getFilterHeatingColumn = flareHeatingStore(useShallow((state) => state.getFilterSetupHeatingValue));
  const filterHeatingValue = flareHeatingStore(useShallow((state) => state.filterHeatingValue));
  const loadingManageHeating = flareHeatingStore(useShallow((state) => state.loadingManageHeating));
  const createSetupHeatingValue = flareHeatingStore(useShallow((state) => state.createSetupHeatingValue));
  const deleteSetupHeatingValue = flareHeatingStore(useShallow((state) => state.deleteSetupHeatingValue));
  const updateSetupHeatingValue = flareHeatingStore(useShallow((state) => state.updateSetupHeatingValue));
  const clearError = flareHeatingStore(useShallow((state) => state.clearError));
  const errorHeating = flareHeatingStore(useShallow((state) => state.errorHeating));
  const clearReportValueByGasName = flareHeatingStore(useShallow((state) => state.clearReportValueByGasName));
  const setHeatingLoading = flareHeatingStore(useShallow((state) => state.setHeatingLoading));
  const loadingAll = flareHeatingStore(useShallow((state) => state.loadingAll));
  const calculateReportAfterUpdateMasterFalreInfo = reportFlareStore(
    useShallow((state) => state.calculateReportAfterUpdateMasterFalreInfo),
  );

  // Text Search
  const [textSearch, setTextSearch] = useState<string>("");
  const [searchTrigger, setSearchTrigger] = useState(0);

  // console.log(" filterHeatingValue : ", filterHeatingValue);

  const onClickEdit = (item: ResponseHeatingValue) => {
    formSetupHeatingValue.setFieldsValue({
      gasName: item.gasName,
      composition: item.composition,
      netHeatingValue: item.netHeatingValue,
      unitHeatingValue: item.unitHeatingValue,
    });
    const tmpManageCompound = [...manageCompound];

    if (item.composition === Composition.SPECIFIC_COMPOSITION) {
      item.specificComposition.forEach((specificCompound) => {
        const uniqKey = uniqueId("update_compound_");
        const compoundId = specificCompound.compound;
        const heatingValueOfRow = (
          item.unitHeatingValue.toLowerCase() === UNITS_ENUM.KCAL_KG.toLowerCase()
            ? specificCompound.compound?.kcalKg
            : specificCompound.compound?.kcalM3
        ) as string;
        const mole = specificCompound.mole;
        tmpManageCompound.push({
          key: uniqKey,
          compound: compoundId.isActive ? compoundId._id : undefined,
          heatingValue: heatingValueOfRow,
          mole: mole,
        });
        formSetupHeatingValue.setFieldsValue({
          [`compound-${uniqKey}`]: compoundId.isActive ? compoundId._id : undefined,
          [`heatingValue-${uniqKey}`]: heatingValueOfRow,
          [`mole-${uniqKey}`]: mole,
        });
      });
    }
    setIsEdit(item);
    setVisibleModal(true);
    setManageCompound(tmpManageCompound);
  };

  const deleteSetting = async (id: string) => {
    const confirm = await VocsModal.delete({
      message: "",
    });
    if (!confirm) return;
    deleteSetupHeatingValue(id).then(() => {
      messageApi.open({
        type: "success",
        content: CommonSuccess.DELETE_SUCCESSFULL,
      });
      clearReportValueByGasName({ heatingGasId: id, year: selectedYear });
      processAfterFinishAction();
    });
  };

  const {
    onChange,
    filter,
    page,
    setPage,
    loading,
    limit: pageSize,
    setLimit: setPageSize,
    data: sourceData,
    replaceFilter,
  } = usePagination<ResponseHeatingValue, FilterSetupHeatingValue>({
    action: searchSetupHeatingValue,
    remoteData: dataHeating.data,
    initialFilter: { year: [selectedYear] },
  });

  const currentFilterExcludePage = useMemo(() => {
    const result = omitBy(filter, (value, key) => key === "page" || key === "year" || key === "limit" || isNull(value));
    return Object.keys(result).length;
  }, [filter]);

  const gasNameFilter = useMemo(() => {
    if (dataHeating.data?.length === 0 && currentFilterExcludePage == 0) return [];
    return filterHeatingValue.gasName.map((item) => {
      return {
        text: item,
        value: item,
      };
    });
  }, [filterHeatingValue.gasName, dataHeating.data]);
  const compositionFilter = useMemo(() => {
    if (dataHeating.data?.length === 0 && currentFilterExcludePage == 0) return [];
    return CompositionDropdown.map((item) => {
      return {
        text: item.label,
        value: item.value,
      };
    });
  }, [CompositionDropdown, dataHeating.data]);
  const netHeatingFilter = useMemo(() => {
    if (dataHeating.data?.length === 0 && currentFilterExcludePage == 0) return [];
    return filterHeatingValue.netHeatingValue.map((item) => {
      return {
        text: item,
        value: item,
      };
    });
  }, [filterHeatingValue.netHeatingValue, dataHeating.data]);

  const handleTableChange: TableProps<ResponseHeatingValue>["onChange"] = (_, filters) => {
    const finalFilters: FilterSetupHeatingValue = {
      ...filters,
      year: filters.year && filters.year.length > 0 ? (filters.year as string[]) : [selectedYear],
    };
    onChange(finalFilters);
  };

  const getIndex = (index: number) => (page - 1) * pageSize + index;
  const columns: TableProps<ResponseHeatingValue>["columns"] = [
    {
      title: "No",
      key: "no",
      render: (_, record, index) => <NumberRow record={record} no={getIndex(index + 1)} />,
      align: "left",
    },
    {
      title: "Gas Name",
      dataIndex: "gasName",
      key: "gasName",
      render: (text) => <Text>{text}</Text>,
      filters: gasNameFilter,
      align: "left",
    },
    {
      title: "Composition",
      dataIndex: "composition",
      key: "composition",
      render: (text) => <Text>{text}</Text>,
      filters: compositionFilter,
      align: "left",
    },
    {
      title: "Net Heating Value",
      dataIndex: "netHeatingValue",
      key: "netHeatingValue",
      render: (text) => <Text>{parseAndFormatScientificNumber(text, 0)}</Text>,
      filters: netHeatingFilter,
      align: "left",
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Space size="middle" direction={"horizontal"}>
          <Button
            className="border-transparent bg-transparent"
            size={"small"}
            onClick={() => onClickEdit(record)}
            disabled={!canEditTable(permissions)}
          >
            <BsPencilSquare size={18} />
          </Button>
          <Button
            className="border-transparent bg-transparent"
            size={"small"}
            onClick={() => deleteSetting(record._id)}
            disabled={!canEditTable(permissions) || record?.isSubmitReport === true}
          >
            <BsTrashFill size={18} />
          </Button>
        </Space>
      ),
      align: "left",
    },
  ];

  // const getColumnFilter = () => {
  //   getFilterHeatingColumn("gasName", selectedYear);
  //   getFilterHeatingColumn("netHeatingValue", selectedYear);
  // };

  const initialRequestDidMount = async (isDidMount?: boolean) => {
    setHeatingLoading(true);
    const getCompoundPromise = getAllSettingFlareCompound({ isActive: true });
    const getFilterPromise = Promise.all([getFilterHeatingColumn(selectedYear)]);
    const searchSetupPromise = searchSetupHeatingValue(
      isDidMount === true ? { ...filter, page, limit: pageSize, year: [selectedYear] } : { page, limit: pageSize, year: [selectedYear] },
    );

    await Promise.all([getCompoundPromise, getFilterPromise, searchSetupPromise]);
    setHeatingLoading(false);
  };

  useDidMount(() => {
    prevCompany.current = profile?.activeCompany._id;
    initialRequestDidMount(true);
  });

  useEffect(() => {
    if (profile?.activeCompany) {
      if (prevCompany.current !== profile.activeCompany._id) {
        replaceFilter({ year: [currentYear.toString()] });
        setSelectedYear(currentYear.toString());
        prevCompany.current = profile?.activeCompany._id;
      }
      setPage(1);
      setTableKey(tableKey + 1);
      setTextSearch("");
    }
  }, [profile?.activeCompany, selectedYear]);

  useEffect(() => {
    if (tableKey > 0) {
      initialRequestDidMount(false);
    }
  }, [tableKey]);

  useEffect(() => {
    searchSetupHeatingValue({ ...filter, textSearch, page: 1, limit: pageSize, year: [selectedYear] });
  }, [searchTrigger]);

  const onChangePage = (val: number) => {
    setPage(val);
  };

  const paginationProps: PaginationProps = {
    size: "small",
    pageSize: pageSize,
    current: page,
    total: totalDocs || 0,
    defaultCurrent: 1,
    onChange: onChangePage,
    showSizeChanger: false,
  };

  const onAddFlareInfo = () => {
    setVisibleModal(true);
  };

  const closeModal = () => {
    setVisibleModal(false);
    formSetupHeatingValue.resetFields();
    clearError("errorHeating");
    setManageCompound([]);
    setIsEdit(null);
  };

  const calculateHeatingValue = (data: ManageCompoundProps[]) => {
    let sumHtvMole = 0;
    data
      .filter((item) => item.compound && item.mole)
      .forEach((item) => {
        const heatingValue: number = parseToDecimal(item.heatingValue);
        const mole: number = parseToDecimal(item.mole);
        const multiply: number = heatingValue * mole;
        sumHtvMole += multiply;
      });
    const result = sumHtvMole / 100;
    return result.toFixed(2);
  };

  const submitSetupHeatingValue = async () => {
    let checkError = false;
    await formSetupHeatingValue.validateFields().catch(() => {
      checkError = true;
    });
    if (checkError) return;

    const formData = formSetupHeatingValue.getFieldsValue();
    // console.log("form data ; ", formData);
    // console.log("manage compound : ", manageCompound);
    const formatData = trimAllKey(replaceUndefinedWithNull(formData), []);
    const finalData: CreateHeatingValue = {
      gasName: formatData.gasName,
      composition: formatData.composition,
      netHeatingValue: formatData.netHeatingValue,
      unitHeatingValue: formatData.unitHeatingValue,
      year: selectedYear.toString(),
      companyId: profile?.activeCompany._id as string,
      specificComposition: [],
      isActive: true,
    };
    if (formData.composition === Composition.SPECIFIC_COMPOSITION) {
      finalData.specificComposition = manageCompound.map((item) => ({ mole: item.mole, compound: item.compound as string }));
    }
    // console.log("Final data : ", finalData);
    if (isEdit) {
      updateSetupHeatingValue({ ...finalData, _id: isEdit._id }).then(onFinishAction);
    } else createSetupHeatingValue(finalData).then(onFinishAction);
  };

  const handleChangeYear = (year: string) => {
    // console.log("Year : ", year);
    replaceFilter({ year: [year] });
    setSelectedYear(year);
  };

  const onFinishAction = (errorMessage?: unknown) => {
    // console.log("errorMessage : ", errorMessage);
    if (errorMessage) {
      // warningAlert(errorMessage as string);
      formSetupHeatingValue.setFields([
        {
          name: "gasName",
          errors: [""],
        },
        {
          name: "composition",
          errors: [""],
        },
      ]);
      return;
    } else {
      if (isEdit && isEdit?._id) {
        calculateReportAfterUpdateMasterFalreInfo({
          key: "gasName",
          _id: isEdit?._id,
        });
      }
      messageApi.open({
        type: "success",
        content: CommonSuccess.SAVED_SUCCESSFULL,
      });
      processAfterFinishAction();
    }
  };
  const processAfterFinishAction = () => {
    getFilterHeatingColumn(selectedYear);
    searchSetupHeatingValue({ ...filter, page, limit: pageSize, year: [selectedYear] });
    closeModal();
  };

  const onAddCompound = async () => {
    const tmpManageCompound = [...manageCompound];
    const uniqKey = uniqueId("new_compound_");
    tmpManageCompound.push({
      key: uniqKey,
      compound: "",
      heatingValue: "",
      mole: "",
    });
    formSetupHeatingValue.setFieldsValue({
      [`compound-${uniqKey}`]: "",
      [`heatingValue-${uniqKey}`]: "",
      [`mole-${uniqKey}`]: "",
    });
    setManageCompound(tmpManageCompound);
  };

  const onUpdateKeyVal = async (rowKey: string, field: keyof ManageCompoundProps, value: string | boolean) => {
    const tmpManageCompound = [...manageCompound];
    const targetIndex = tmpManageCompound.findIndex((item) => item.key === rowKey);
    if (targetIndex !== -1) {
      tmpManageCompound[targetIndex][field] = value as never;
    }
    setManageCompound(tmpManageCompound);
    if (field === "compound" || field === "mole") {
      const resultHeatingVal = calculateHeatingValue(tmpManageCompound);
      formSetupHeatingValue.setFieldValue("netHeatingValue", resultHeatingVal);
    }
  };

  const onDeleteManageCompound = (index: number | string) => {
    const tmpManageCompound = [...manageCompound];
    tmpManageCompound.splice(+index, 1);
    const resultHeatingValue = calculateHeatingValue(tmpManageCompound);
    formSetupHeatingValue.setFieldValue("netHeatingValue", resultHeatingValue);
    setManageCompound(tmpManageCompound);
    formSetupHeatingValue.validateFields({ dirty: true }).catch(() => console.log());
  };

  const processRowClassName = useCallback(
    (record: ResponseHeatingValue) => {
      if (record.composition === Composition.SPECIFIC_HEATING_VALUE) return "";

      const hasFullComposition = record.specificComposition.every((item) => item.compound.isActive === true);
      return !hasFullComposition ? "red-border-row" : "";
    },
    [selectedYear, sourceData],
  );

  const onSearch: SearchProps["onSearch"] = (value, _e) => {
    setTextSearch(value);
    setSearchTrigger((prev) => prev + 1);
  };

  return (
    <Row className="w-full" id={"wrap-heating-value-table"}>
      {contextHolder}
      <Row className="w-full" justify={"space-between"}>
        <Select value={selectedYear} style={{ width: 120 }} onChange={handleChangeYear} options={optionsSelectYears()} size="middle" />
        <Search onSearch={onSearch} style={{ width: 250 }} value={textSearch} onChange={(e) => setTextSearch(e.target.value)} allowClear />
        <Button
          type={"primary"}
          size={"middle"}
          className="flex items-center"
          onClick={onAddFlareInfo}
          style={{ visibility: canEditTable(permissions) ? "visible" : "hidden" }}
        >
          <PlusCircleOutlined />
          New
        </Button>
      </Row>
      <Table
        key={tableKey}
        columns={addCenterAlign(columns)}
        dataSource={sourceData as ResponseHeatingValue[]}
        className="w-full mt-4"
        loading={loadingProfile || loadingAll || loading || loadingHeating}
        pagination={false}
        onChange={handleTableChange}
        rowKey={(rec) => `${rec._id}`}
        rowClassName={processRowClassName} // Apply red border conditionally
      />

      <Row className="w-full mt-4" justify={"end"}>
        <Pagination {...paginationProps} />
      </Row>

      <HeatingModal
        submitSetupHeatingValue={submitSetupHeatingValue}
        loadingManageHeating={loadingManageHeating}
        visibleModal={visibleModal}
        closeModal={closeModal}
        errorHeating={errorHeating}
        formSetupHeatingValue={formSetupHeatingValue}
        onAddCompound={onAddCompound}
        onDeleteManageCompound={onDeleteManageCompound}
        manageCompound={manageCompound}
        calculateHeatingValue={calculateHeatingValue}
        onUpdateKeyVal={onUpdateKeyVal}
        setManageCompound={setManageCompound}
        clearErrorHeating={clearError}
      />
    </Row>
  );
};

export default HeatingValueTable;
